/* eslint-disable complexity */
import { type FC, useMemo, useState } from 'react';

import {
  Box,
  Button,
  type ButtonProps,
  Center,
  Collapse,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Stack,
  Text,
  VStack,
  useControllableState,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  type DateRange,
  type DayModifiers,
  type DayPickerProps,
  type Matcher,
  type ModifiersStyles,
  DayPicker as ReactDayPicker,
} from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { LuRefreshCw } from 'react-icons/lu';
import { Margin, usePDF } from 'react-to-pdf';

import {
  DownloadIcon,
  IconAiSpecial,
  IconCalendarOutline,
  IconDotsVertical,
  IconExcelExport,
} from '@/components';
import { BackButton } from '@/components/BackButton';
import { Caption, Day } from '@/components/DayPicker/_partials';
import {
  DEFAULT_MODIFIERS,
  DEFAULT_MODIFIERS_STYLES,
} from '@/components/DayPicker/constants';
import { useDayPickerMonthNavigation } from '@/components/DayPicker/hooks/useDayPickerMonthNavigation';
import { useDateRangeQueryState } from '@/components/Filters/Date/hooks';
import IconArrowDown from '@/components/Icons/icons-generated/IconArrowDown';
import { Markdown } from '@/components/Markdown';
import i18n from '@/lib/i18n/client';
import { AVAILABLE_LANGUAGES } from '@/lib/i18n/constants';
import {
  type InterviewReportDto,
  type QuestionReportDto,
  QuestionType,
} from '@/services/api';

import { AdminLayoutPage, AdminLayoutPageContent } from '../admin';
import { usePageAdminInterviewContext } from '../admin-interview/PageAdminInterview.context';
import { useAdminInterviewPageParams } from '../admin-interview/PageAdminInterview.helper';
import {
  InterviewKeys,
  useExportCandidatesMutation,
  useGetInterviewReport,
  useReportQuestionSummaryMutation,
} from '../admin-new-interview';
import { useGetAccountQuery } from '../auth';
import { BarChart } from './charts/BarChart';
import { PieChart } from './charts/PieChart';
import { QuestionSummaryCharts } from './charts/SummaryBarChart';

dayjs.extend(utc);

type PageAdminInterviewsReportProps = {
  isInContext?: boolean;
};

export const PageAdminInterviewsReport: FC<PageAdminInterviewsReportProps> = ({
  isInContext = false,
}) => {
  const contextInterviewId = usePageAdminInterviewContext()?.interviewId;
  const paramsInterviewId = useAdminInterviewPageParams()?.interviewId;

  const interviewId = isInContext ? contextInterviewId : paramsInterviewId;

  const [dateRangeSearchTerm, setDateRangeSearchTerm] =
    useDateRangeQueryState();

  const { data, isLoading } = useGetInterviewReport(
    interviewId,
    dateRangeSearchTerm
  );

  if (isLoading) {
    return (
      <Center flex={1} w="full">
        <Spinner size="sm" />
      </Center>
    );
  }

  if (!data) return null;

  if (isInContext)
    return (
      <ReportContent
        inContext
        data={data}
        isLoading={isLoading}
        dateRangeSearchTerm={dateRangeSearchTerm}
        setDateRangeSearchTerm={setDateRangeSearchTerm}
        interviewId={data.interviewId}
      />
    );

  return (
    <AdminLayoutPage>
      <AdminLayoutPageContent
        containerMaxWidth="none"
        pageContainerProps={{ px: 0 }}
        p={0}
      >
        <ReportContent
          inContext={false}
          data={data}
          isLoading={isLoading}
          dateRangeSearchTerm={dateRangeSearchTerm}
          setDateRangeSearchTerm={setDateRangeSearchTerm}
          interviewId={data.interviewId}
        />
      </AdminLayoutPageContent>
    </AdminLayoutPage>
  );
};

type ReportContentProps = {
  inContext?: boolean;
  data: InterviewReportDto;
  setDateRangeSearchTerm: (
    value: DateRange | ((old: DateRange) => DateRange | null) | null
  ) => Promise<URLSearchParams>;
  dateRangeSearchTerm: DateRange;
  interviewId: string | undefined;
  isLoading: boolean;
  isShare?: boolean;
};

export const ReportContent: FC<ReportContentProps> = ({
  data,
  interviewId,
  isShare = false,
  dateRangeSearchTerm,
  setDateRangeSearchTerm,
  inContext = false,
}) => {
  const { t } = useTranslation(['common', 'cv', 'interview']);
  const toast = useToast();
  const { toPDF, targetRef } = usePDF({
    filename: data.title + '-report.pdf',
  });

  const exportExcelCandidatesMutation = useExportCandidatesMutation();

  const handleExportReportToExcel = async () => {
    try {
      if (!interviewId) return;

      const s3Url = await exportExcelCandidatesMutation.mutateAsync({
        interviewsId: interviewId,
        dateFrom: dateRangeSearchTerm.from,
        dateTo: dateRangeSearchTerm.to,
      });

      window.open(s3Url, '_blank');
    } catch (error) {
      toast({
        title: t('common:errors.downloadFileFailed'),
        status: 'error',
      });
    }
  };

  return (
    <>
      <Box
        border="1px solid"
        borderRadius={12}
        bg="white"
        borderColor="gray.50"
        mb={inContext ? 0 : 1}
      >
        <HStack
          py={4}
          px={inContext ? 10 : 20}
          maxWidth="1440px"
          borderBottom="1px solid"
          borderBottomColor="gray.50"
          justifyContent="space-between"
        >
          {inContext ? (
            <Text fontWeight="semibold" fontSize="2xl" color="gray.900">
              {t('common:report')}
            </Text>
          ) : isShare ? (
            <Text fontWeight="semibold" fontSize="2xl" color="gray.900">
              {data.title}
            </Text>
          ) : (
            <BackButton
              color="black"
              customText={data.title + ' ' + t('common:report')}
              wrapperStyles={{
                mb: 0,
                color: 'gray.900',
                fontWeight: 'semibold',
                fontSize: '2xl',
              }}
            />
          )}
          {/* TODO: uncomment when required */}
          {/* <HStack>
            {!isShare && (
              <ReportShareMenu
                reportLink={data.reportLink}
                isLoading={isLoading}
              />
            )} */}
          <HStack>
            <ReportDateRange
              setDateRangeSearchTerm={setDateRangeSearchTerm}
              dateRangeSearchTerm={dateRangeSearchTerm}
            />
            <Menu placement="bottom-end">
              <MenuButton
                variant="outline"
                w={12}
                as={Button}
                justifyContent="center"
                display="flex"
                isLoading={exportExcelCandidatesMutation.isPending}
              >
                <IconDotsVertical w={2} fontSize={20} mr={3} />
              </MenuButton>

              <MenuList
                overflow="hidden"
                borderColor="gray.50"
                w={10}
                shadow="md"
                borderRadius={12}
                p={0}
              >
                <VStack spacing={0}>
                  <MenuItem
                    w="full"
                    py={3}
                    transition="linear 0.1s"
                    borderRadius={8}
                    color="gray.900"
                    fontWeight="medium"
                    borderBottomRadius={0}
                    onClick={handleExportReportToExcel}
                    icon={<IconExcelExport fontSize="lg" color="gray.600" />}
                  >
                    {t('interview:openInExcel')}
                  </MenuItem>
                  <MenuItem
                    w="full"
                    py={3}
                    transition="linear 0.1s"
                    borderRadius={8}
                    borderTopRadius={0}
                    color="gray.900"
                    fontWeight="medium"
                    onClick={() =>
                      toPDF({
                        overrides: {
                          canvas: { useCORS: true },
                        },
                        page: { margin: Margin.LARGE, orientation: 'portrait' },
                      })
                    }
                    icon={<DownloadIcon fontSize={20} />}
                  >
                    {t('cv:result.report')}
                  </MenuItem>
                </VStack>
              </MenuList>
            </Menu>
          </HStack>
        </HStack>
      </Box>
      <Stack mb={10} ref={targetRef} mt={inContext ? 0 : 3}>
        <QuestionSummaryCharts
          questions={
            data.questions?.map((q) => {
              return {
                question: q.question ?? '',
                choiceStatistics: [
                  ...(q.tagStatistics ?? []).map((t) => ({
                    choice: t.tag,
                    count: t.count,
                  })),
                ],
              };
            }) ?? []
          }
        />

        {data.questions?.map((q, index) => (
          <QuestionReport
            key={q.question}
            question={q}
            index={index}
            questionsLength={data?.questions?.length ?? 0}
            interviewId={interviewId}
            dateRangeSearchTerm={dateRangeSearchTerm}
          />
        ))}
      </Stack>
    </>
  );
};

type QuestionReportProps = {
  question: QuestionReportDto;
  index: number;
  questionsLength: number;
  interviewId: string | undefined;
  dateRangeSearchTerm: DateRange;
};

const QuestionReport: React.FC<QuestionReportProps> = (props) => {
  const { t } = useTranslation(['common', 'interview']);
  const queryClient = useQueryClient();
  const reportQuestionSummary = useReportQuestionSummaryMutation({
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: InterviewKeys.reportShareQueryKey({
          interviewsId: props.interviewId,
          dateRange: props.dateRangeSearchTerm,
        }),
        exact: false,
      });
    },
  });
  const [isShowAnswer, setIsShowAnswer] = useState(false);
  const account = useGetAccountQuery();

  const handleGenerateAiSummary = async () => {
    if (
      !account.data?.email ||
      !props.interviewId ||
      !props.question.questionId
    )
      return;

    await reportQuestionSummary.mutateAsync({
      interviewsId: props.interviewId,
      questionId: props.question.questionId,
      email: account.data.email,
    });
  };

  const answerCount =
    props.question.answerList?.filter((a) => !!a)?.length ??
    !!props.question.choiceStatistics?.some((a) => !!a.count);

  return (
    <Stack
      bg="white"
      borderRadius="xl"
      borderWidth={1}
      borderColor="gray.50"
      w="full"
      p={5}
    >
      <HStack justifyContent="space-between" alignItems="center">
        <Text fontSize="sm" color="gray.500" mb={1}>
          {t('common:detail.question').toAppLocaleUpperCase() +
            ' ' +
            (props.index + 1) +
            '/' +
            props.questionsLength}
        </Text>
        <HStack alignItems="center">
          {answerCount && (
            <Text fontSize="sm" color="gray.500">
              {answerCount} {t('common:answer')}
            </Text>
          )}
        </HStack>
      </HStack>
      <Text mt={1} mb={2} fontWeight="semibold">
        {props.question.question}
      </Text>

      <Stack>
        {!answerCount &&
          (props.question.questionType === QuestionType.MultipleSelection ||
            props.question.questionType === QuestionType.MultipleChoice) && (
            <Stack w="full" justifyContent="center" alignItems="center">
              <Text color="gray.500">{t('interview:noAnswer')}</Text>
            </Stack>
          )}

        {props.question.questionType === QuestionType.MultipleChoice &&
          answerCount && (
            <HStack
              mx="auto"
              borderWidth="1px"
              borderRadius="xl"
              p={2}
              borderColor="gray.50"
              w="full"
              justifyContent="center"
            >
              <PieChart
                percent={
                  props.question.choiceStatistics?.map(
                    (a) => a.percentage ?? 0
                  ) ?? []
                }
                series={
                  props.question.choiceStatistics?.map((a) => a.count ?? 0) ??
                  []
                }
                labels={
                  props.question?.choiceStatistics
                    ?.map((c) => c.choice)
                    .filter(
                      (choice): choice is string => choice !== undefined
                    ) ?? []
                }
              />
            </HStack>
          )}

        {(props.question.questionType === QuestionType.Video ||
          props.question.questionType === QuestionType.OpenEnded) &&
          answerCount &&
          (props.question.tagStatistics?.length ?? 0) > 0 && (
            <HStack
              mx="auto"
              borderWidth="1px"
              borderRadius="xl"
              p={2}
              borderColor="gray.50"
              w="full"
              justifyContent="center"
            >
              <BarChart
                percent={
                  props.question.tagStatistics?.map((a) => a.percentage ?? 0) ??
                  []
                }
                data={
                  props.question.tagStatistics?.map((a) => a.count ?? 0) ?? []
                }
                labels={
                  props.question?.tagStatistics
                    ?.map((c) => c.tag)
                    .filter((tag): tag is string => tag !== undefined) ?? []
                }
              />
            </HStack>
          )}

        {props.question.questionType === QuestionType.MultipleSelection &&
          answerCount && (
            <HStack
              mx="auto"
              borderWidth="1px"
              borderRadius="xl"
              p={2}
              borderColor="gray.50"
              w="full"
              justifyContent="center"
            >
              <BarChart
                percent={
                  props.question.choiceStatistics?.map(
                    (a) => a.percentage ?? 0
                  ) ?? []
                }
                data={
                  props.question.choiceStatistics?.map((a) => a.count ?? 0) ??
                  []
                }
                labels={
                  props.question?.choiceStatistics
                    ?.map((c) => c.choice)
                    .filter(
                      (choice): choice is string => choice !== undefined
                    ) ?? []
                }
              />
            </HStack>
          )}
      </Stack>

      {answerCount &&
        (props.question.aiReportSummary ? (
          <Stack
            borderColor="primary.50"
            borderWidth={1}
            py={1}
            rounded="lg"
            bg="primary.25"
          >
            <HStack
              borderBottom="1px solid"
              borderColor="primary.50"
              px={4}
              pb={1}
            >
              <IconAiSpecial color="primary.600" fontSize="24px" />
              <Text color="primary.500" fontWeight="semibold">
                {t('interview:videoInterview.aiSummary') + ': '}
              </Text>
            </HStack>
            <Markdown color="primary.900" px={3} pb={1} maxLines={10}>
              {props.question.aiReportSummary}
            </Markdown>
          </Stack>
        ) : (
          <Button
            my={5}
            mx="auto"
            w="fit-content"
            leftIcon={<LuRefreshCw />}
            onClick={handleGenerateAiSummary}
            isLoading={reportQuestionSummary.isPending}
          >
            {t('common:aiSummary')}
          </Button>
        ))}

      {props.question.questionType !== QuestionType.MultipleSelection &&
        props.question.questionType !== QuestionType.MultipleChoice && (
          <>
            <Button
              colorScheme="primary"
              variant="link"
              fontSize="sm"
              textDecoration="underline"
              onClick={() => setIsShowAnswer(!isShowAnswer)}
            >
              {isShowAnswer
                ? t('common:actions.hideAnswers')
                : t('common:actions.showAnswers')}
            </Button>
            <Collapse in={isShowAnswer} animateOpacity>
              <QuestionAnswerReport
                answerList={props.question.answerList ?? []}
              />
            </Collapse>
          </>
        )}
    </Stack>
  );
};

type ReportDateRangeProps = {
  dateRangeSearchTerm: DateRange;
  setDateRangeSearchTerm: (
    value: DateRange | ((old: DateRange) => DateRange | null) | null
  ) => Promise<URLSearchParams>;
};

const ReportDateRange: FC<ReportDateRangeProps> = (props) => {
  const [fromDayValue, setFromDayValue] = useState<Date | undefined>(undefined);
  const [toDayValue, setToDayValue] = useState<Date | undefined>(undefined);
  const popoverDisclosure = useDisclosure();

  const onSubmitDate = async (
    fromDate: Date | undefined,
    toDate: Date | undefined
  ) => {
    props.setDateRangeSearchTerm({ from: fromDate, to: toDate });
    popoverDisclosure.onClose();
  };

  return (
    <Popover
      isOpen={popoverDisclosure.isOpen}
      onClose={popoverDisclosure.onClose}
      placement="left-start"
    >
      <PopoverTrigger>
        <Button
          onClick={() => popoverDisclosure.onOpen()}
          leftIcon={<IconCalendarOutline fontSize={24} />}
          rightIcon={
            <IconArrowDown
              color="gray.300"
              transform={
                popoverDisclosure.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
              }
              transition="transform 0.2s ease-in-out"
            />
          }
        >
          {props?.dateRangeSearchTerm.from
            ? props?.dateRangeSearchTerm.from?.toLocaleDateString()
            : ''}
          {props.dateRangeSearchTerm.from &&
            props.dateRangeSearchTerm.to &&
            ' - '}
          {props?.dateRangeSearchTerm.to
            ? props?.dateRangeSearchTerm.to?.toLocaleDateString()
            : ''}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        borderWidth={0}
        mt={14}
        mr={48}
        display="flex"
        flexDir="row"
      >
        <PopoverDayPicker
          wrappedInPopoverContent
          defaultFromDayValue={dayjs(new Date()).subtract(1, 'month').toDate()}
          defaultToDayValue={props.dateRangeSearchTerm.to}
          fromDayValue={fromDayValue}
          toDayValue={toDayValue}
          setToDayValue={(date) => setToDayValue(date)}
          setFromDayValue={(date) => setFromDayValue(date)}
          onSubmit={onSubmitDate}
        />
      </PopoverContent>
    </Popover>
  );
};

type QuestionAnswerReportProps = {
  answerList: Array<string>;
};

const QuestionAnswerReport: FC<QuestionAnswerReportProps> = ({
  answerList,
}) => {
  const { t } = useTranslation(['common', 'interview']);

  if (answerList.length === 0 || answerList.every((a) => a === '')) {
    return (
      <Stack w="full" justifyContent="center" alignItems="center">
        <Text color="gray.500">{t('interview:noAnswer')}</Text>
      </Stack>
    );
  }

  return (
    <Stack>
      {answerList.map(
        (a) =>
          !!a && (
            <Text
              key={a}
              borderRadius="xl"
              borderWidth={1}
              borderColor="gray.50"
              p={3}
              bg="gray.25"
            >
              {a}
            </Text>
          )
      )}
    </Stack>
  );
};

export const dateRangeDefaultValue: DateRange = {
  from: undefined,
  to: undefined,
};

type InputDayPickerBaseProps = {
  toDayValue: Date | undefined;
  fromDayValue: Date | undefined;
  setToDayValue: (singleDayValue: Date | undefined) => void;
  setFromDayValue: (singleDayValue: Date | undefined) => void;
  onSubmit: (fromDate: Date | undefined, toDate: Date | undefined) => void;
  defaultToDayValue?: Date;
  defaultFromDayValue?: Date;
  submitButtonProps?: Omit<ButtonProps, 'onClick'>;
  isLoading?: boolean;
  notBefore?: Date;
  notAfter?: Date;
  wrappedInPopoverContent?: boolean;
  canDeselectSelectedDate?: boolean;
  isFilter?: boolean;
};

export const PopoverDayPicker = (props: InputDayPickerBaseProps) => {
  const { t } = useTranslation();
  const hookFromMonthNavigation = useDayPickerMonthNavigation(
    props.defaultFromDayValue
  );
  const hookToMonthNavigation = useDayPickerMonthNavigation();
  const {
    setMonth: setToMonth,
    toggleMode: toggleToMode,
    month: toMonth,
  } = hookToMonthNavigation;

  const {
    setMonth: setFromMonth,
    toggleMode: toggleFromMode,
    month: fromMonth,
  } = hookFromMonthNavigation;

  const [fromDayValue, setFromDayValue] = useControllableState<
    Date | undefined
  >({
    value: undefined,
    defaultValue: props.defaultFromDayValue,
    onChange: props.setFromDayValue,
  });

  const [toDayValue, setToDayValue] = useControllableState<Date | undefined>({
    value: undefined,
    defaultValue: props.defaultToDayValue,
    onChange: props.setToDayValue,
  });

  const handleChangeFromMonth = (date?: Date | null) => {
    setFromMonth(date);
  };

  const handleChangeToMonth = (date?: Date | null) => {
    setToMonth(date);
  };

  const modifiers: DayModifiers = {
    ...DEFAULT_MODIFIERS,
  };

  const modifiersStyles: ModifiersStyles = {
    ...DEFAULT_MODIFIERS_STYLES,
  };

  const footer = (
    <HStack mt="2">
      <Button
        isLoading={props.isLoading}
        onClick={() => props.onSubmit?.(fromDayValue, toDayValue)}
        variant="@primary"
        size="sm"
        w="full"
        {...props.submitButtonProps}
      >
        {props.isFilter ? t('actions.applyFilter') : t('actions.done')}
      </Button>

      <Button
        isLoading={props.isLoading}
        size="sm"
        onClick={() => {
          setFromDayValue(undefined);
          setToDayValue(undefined);
        }}
      >
        {t('actions.clear')}
      </Button>
    </HStack>
  );

  const disabledDays = useMemo(() => {
    const disabledDays: Array<Matcher> = [];

    if (props.notBefore) {
      disabledDays.push({
        before: dayjs(props.notBefore).startOf('day').toDate(),
      });
    }

    if (props.notAfter) {
      disabledDays.push({
        after: dayjs(props.notAfter).startOf('day').toDate(),
      });
    }

    return disabledDays;
  }, [props.notAfter, props.notBefore]);

  const fromDayPickerProps: DayPickerProps = {
    initialFocus: true,
    disabled: disabledDays,
    month: fromMonth ?? undefined,
    onMonthChange: handleChangeFromMonth,
    components: {
      Caption: (props) => (
        <Caption
          {...props}
          onCaptionLabelClick={toggleFromMode}
          setMonth={(date) => {
            handleChangeFromMonth(date);
          }}
        />
      ),
      Day,
    },
    modifiers,
    modifiersStyles,
    locale: AVAILABLE_LANGUAGES.find((x) => x.key === i18n.language)
      ?.dataFnsLocale,
  };

  const toDayPickerProps: DayPickerProps = {
    initialFocus: true,
    disabled: disabledDays,
    month: toMonth ?? undefined,
    onMonthChange: handleChangeToMonth,
    components: {
      Caption: (props) => (
        <Caption
          {...props}
          onCaptionLabelClick={toggleToMode}
          setMonth={(date) => {
            handleChangeToMonth(date);
          }}
        />
      ),
      Day,
    },
    modifiers,
    modifiersStyles,
    locale: AVAILABLE_LANGUAGES.find((x) => x.key === i18n.language)
      ?.dataFnsLocale,
  };

  const handleFromSelectSingleDate = (selectedDate: Date | undefined) => {
    const utcDate = selectedDate
      ? dayjs(selectedDate).utc().toDate()
      : undefined;
    setFromDayValue(utcDate);
    setFromMonth(utcDate);
  };

  const handleToSelectSingleDate = (selectedDate: Date | undefined) => {
    const utcDate = selectedDate
      ? dayjs(selectedDate).utc().toDate()
      : undefined;
    setToDayValue(utcDate);
    setToMonth(utcDate);
  };

  return (
    <HStack
      bg="white"
      p={2}
      alignItems="start"
      borderWidth={1}
      borderRadius="lg"
    >
      <ReactDayPicker
        {...fromDayPickerProps}
        footer={<HStack h={10} />}
        mode="single"
        selected={fromDayValue}
        defaultMonth={fromDayValue}
        onSelect={handleFromSelectSingleDate}
      />
      <ReactDayPicker
        {...{ ...toDayPickerProps, footer }}
        mode="single"
        selected={toDayValue}
        onSelect={handleToSelectSingleDate}
      />
    </HStack>
  );
};
